import type { FC, ReactNode } from "react";
import {
  Autocomplete,
  Popover,
  type PopoverProps,
} from "react-aria-components";
import { MenuSearchField } from "./menuSearchField.js";
import { menuStyle } from "./menuStyle.js";
import { longMenuMaxHeight } from "./simpleMenuPopover.js";

export interface SearchableMenuPopoverProps extends Omit<
  PopoverProps,
  "className" | "children" | "maxHeight"
> {
  /** The option list, typically a `ListBox`. Scrolls; must not scroll itself. */
  children: ReactNode;
  /** Optional className applied to the Popover. Use to set the menu width. */
  className?: string;
  /** Accessible label for the search field, such as "Search values". */
  searchAriaLabel: string;
  searchPlaceholder?: string;
  searchMaxLength?: number;
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Shows a spinner beside the search field while options are loading. */
  isLoading?: boolean;
  /** Note pinned below the options, such as why the list is truncated. */
  note?: ReactNode;
}

/**
 * A menu whose options are filtered by a search field, for option lists too
 * long to scan. The field and the note stay put while the options scroll, and
 * only the options scroll, so the scrollbar sits against the popover's edge
 * with the options padded away from it.
 */
export const SearchableMenuPopover: FC<SearchableMenuPopoverProps> = ({
  children,
  className,
  searchAriaLabel,
  searchPlaceholder,
  searchMaxLength,
  searchValue,
  onSearchChange,
  isLoading,
  note,
  ...props
}) => (
  <Popover
    className={menuStyle().popover({ className })}
    maxHeight={longMenuMaxHeight}
    {...props}
  >
    <div className="flex max-h-[inherit] flex-col overflow-hidden rounded-[inherit] bg-white">
      <Autocomplete
        inputValue={searchValue}
        onInputChange={onSearchChange}
        disableAutoFocusFirst
      >
        <MenuSearchField
          aria-label={searchAriaLabel}
          placeholder={searchPlaceholder}
          maxLength={searchMaxLength}
          isLoading={isLoading}
        />

        <div
          className={menuStyle().scrollContainer({
            className: "max-h-none min-h-0 flex-1",
          })}
        >
          {children}
        </div>

        {note === undefined ? null : (
          <div className="shrink-0 px-1 pb-1">
            {/* The options' scroll container supplies the space above the rule. */}
            <div className={menuStyle().separator({ className: "mt-0" })} />
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              {note}
            </div>
          </div>
        )}
      </Autocomplete>
    </div>
  </Popover>
);
